import { useEffect, useRef, useState } from 'react';

import { requestZeusReadFromBrowser } from 'components/api/zeusBrowserClient';
import { MarketplaceBrowseMapDocument } from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';
import type { MarketplaceBrowseMapQueryVariables } from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';
import { normalizeSearchVertices } from 'components/marketplace/helpers/useSearchQueries';
import type { PropertySearchQuery } from 'components/marketplace/helpers/useSearchQueries';

import type { MarketShape } from '../types';
import { MAX_MERCATOR_LATITUDE } from './clusterCamera';
import type { MapEnvelope } from './pins';
import { reportMapError } from './reportMapError';

/** Where the visitor is. */
export type VisitorLocation = { latitude: number; longitude: number };

type MapFilters = MarketplaceBrowseMapQueryVariables['input']['filters'];

/** Where Cloudflare places the visitor; the edge worker serves it on the site's own origin. */
const VISITOR_LOCATION_PATH = '/_od/cf';
/** The area around the visitor the map opens on, in degrees: about 7 miles, as on iOS. */
const START_SPAN_DEGREES = 0.1;
/** Wider areas searched together when the start area has no homes; as on iOS. */
const SEARCH_SPANS_DEGREES = [1, 8, 64];
/** Room around the nearest home when the map frames it with the visitor; as on iOS. */
const NEAREST_HOME_MARGIN = 1.4;
/** How long the start area may take before the map opens on the market. */
const START_AREA_BUDGET_MS = 4000;
const GEOLOCATION_TIMEOUT_MS = 10_000;
/**
 * How long the show-my-location button waits for the browser. Its own timeout
 * starts only once the visitor answers the prompt, which may never happen.
 */
const LOCATE_BUDGET_MS = 15_000;
const GEOLOCATION_MAX_AGE_MS = 60_000;

/**
 * Whether the page opens on its plain market view: the address asks for no
 * search area, results page, saved search, or home instead.
 */
export const opensOnMarketView = ({
  query,
  zoneIds,
  savedSearchId,
  propertyRoute,
}: {
  query: Pick<PropertySearchQuery, 'vertices' | 'page' | 'preview_aid'>;
  /** Zones the address asks for, as a zip route or `zoneIds` does. */
  zoneIds: readonly string[] | null;
  savedSearchId: string | null;
  propertyRoute: boolean;
}): boolean =>
  !propertyRoute &&
  !savedSearchId &&
  !zoneIds?.length &&
  !query.page &&
  !query.preview_aid &&
  !normalizeSearchVertices(query.vertices);

const readVisitorLocation = (body: unknown): VisitorLocation | null => {
  if (typeof body !== 'object' || body === null || !('cf' in body)) return null;
  const { cf } = body;
  if (typeof cf !== 'object' || cf === null) return null;
  const latitude = Number('latitude' in cf ? cf.latitude : NaN);
  const longitude = Number('longitude' in cf ? cf.longitude : NaN);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude === 0 && longitude === 0) return null;
  return { latitude, longitude };
};

/**
 * Where Cloudflare places the visitor, about to their city. Null when it is
 * unknown, as without the edge worker. The answer needs no cookies, and it
 * belongs to this visitor and moment.
 */
export const fetchVisitorLocation = async (
  signal?: AbortSignal,
): Promise<VisitorLocation | null> => {
  if (typeof fetch !== 'function') return null;
  try {
    const response = await fetch(VISITOR_LOCATION_PATH, {
      credentials: 'omit',
      cache: 'no-store',
      headers: { accept: 'application/json' },
      signal,
    });
    return response.ok ? readVisitorLocation(await response.json()) : null;
  } catch {
    return null;
  }
};

/**
 * Where the visitor is for the show-my-location button: the browser's own
 * location when they allow it, else where Cloudflare places them.
 */
export const locateVisitor = (): Promise<VisitorLocation | null> =>
  new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(fetchVisitorLocation());
      return;
    }
    let answered = false;
    const answer = (visitor: VisitorLocation | null | Promise<VisitorLocation | null>) => {
      if (answered) return;
      answered = true;
      clearTimeout(budget);
      resolve(visitor);
    };
    const budget = setTimeout(() => answer(fetchVisitorLocation()), LOCATE_BUDGET_MS);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => answer({ latitude: coords.latitude, longitude: coords.longitude }),
      () => answer(fetchVisitorLocation()),
      { timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: GEOLOCATION_MAX_AGE_MS },
    );
  });

/** Whether a point is inside the shape's outline: inside an odd number of its rings. */
const shapeContains = (shape: MarketShape, { latitude, longitude }: VisitorLocation) => {
  let inside = false;
  for (const ring of shape.coordinates) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      const a = ring[i];
      const b = ring[j];
      if (
        a &&
        b &&
        a.lat > latitude !== b.lat > latitude &&
        longitude < ((b.lon - a.lon) * (latitude - a.lat)) / (b.lat - a.lat) + a.lon
      ) {
        inside = !inside;
      }
    }
  }
  return inside;
};

/**
 * Whether the visitor is inside the market: inside its bounds and, when the
 * market is drawn as an outline, inside that too. A market given as two
 * corners is only its bounds.
 */
export const isInsideMarket = (
  visitor: VisitorLocation,
  bounds: MapEnvelope,
  shape: MarketShape,
): boolean => {
  const { latitude, longitude } = visitor;
  if (
    latitude < bounds.south ||
    latitude > bounds.north ||
    longitude < bounds.west ||
    longitude > bounds.east
  ) {
    return false;
  }
  const outlined = shape.coordinates.some((ring) => ring.length >= 4);
  return !outlined || shapeContains(shape, visitor);
};

/** A box of `span` degrees on each side, centered on a point and kept on the map. */
const boxAround = (
  { latitude, longitude }: VisitorLocation,
  latSpan: number,
  lonSpan = latSpan,
) => ({
  north: Math.min(MAX_MERCATOR_LATITUDE, latitude + latSpan / 2),
  south: Math.max(-MAX_MERCATOR_LATITUDE, latitude - latSpan / 2),
  east: Math.min(180, longitude + lonSpan / 2),
  west: Math.max(-180, longitude - lonSpan / 2),
});

/**
 * Where the homes in an area are, each as a box that holds at least one home:
 * a marker's point, the home of a one-home group, or the box of a larger group.
 */
const homesIn = async (
  area: MapEnvelope,
  filters: MapFilters,
  signal: AbortSignal,
): Promise<MapEnvelope[]> => {
  const { north, south, east, west } = area;
  const vertices = [
    { lat: north, lon: west },
    { lat: north, lon: east },
    { lat: south, lon: east },
    { lat: south, lon: west },
    { lat: north, lon: west },
  ];
  const data = await requestZeusReadFromBrowser(
    MarketplaceBrowseMapDocument,
    {
      input: { filters, mode: { adaptive: { viewport: vertices, area: { vertices } } } },
      includePhotos: false,
    },
    signal,
  );
  const point = ({ latitude, longitude }: VisitorLocation): MapEnvelope => ({
    north: latitude,
    south: latitude,
    east: longitude,
    west: longitude,
  });
  const result = data.marketplace.mapSearch;
  if (result.__typename === 'MarketplaceMapSearchMarkersResult') {
    return result.markers.map(({ location }) => point(location));
  }
  if (result.__typename === 'MarketplaceMapSearchClustersResult') {
    // A group's center can fall between towns, so it stands for no home.
    return result.clusters
      .filter((cluster) => cluster.listingCount > 0)
      .map((cluster) =>
        cluster.listing
          ? point(cluster.listing.location)
          : (cluster.envelope ?? point(cluster.location)),
      );
  }
  return [];
};

/**
 * Where the map opens for a visitor, as the iOS app does: the area of about a
 * city around them. When it has no homes, it searches wider areas around them,
 * all at once, and frames them with the nearest homes in the smallest area
 * that has any. Null when none of those areas has homes.
 */
export const findStartArea = async (
  visitor: VisitorLocation,
  filters: MapFilters,
  signal: AbortSignal,
): Promise<MapEnvelope | null> => {
  const start = boxAround(visitor, START_SPAN_DEGREES);
  if ((await homesIn(start, filters, signal)).length > 0) return start;
  const wider = await Promise.all(
    SEARCH_SPANS_DEGREES.map((span) => homesIn(boxAround(visitor, span), filters, signal)),
  );
  const lonScale = Math.cos((visitor.latitude * Math.PI) / 180);
  /** How far the visitor is from the nearest point of a box of homes. */
  const distance = (box: MapEnvelope) =>
    Math.hypot(
      Math.min(box.north, Math.max(box.south, visitor.latitude)) - visitor.latitude,
      (Math.min(box.east, Math.max(box.west, visitor.longitude)) - visitor.longitude) * lonScale,
    );
  for (const homes of wider) {
    const nearest = homes.reduce<MapEnvelope | null>(
      (best, box) => (!best || distance(box) < distance(best) ? box : best),
      null,
    );
    if (nearest) {
      // Centered on the visitor, and wide enough for the whole box of homes.
      const latReach = Math.max(
        Math.abs(nearest.north - visitor.latitude),
        Math.abs(nearest.south - visitor.latitude),
      );
      const lonReach = Math.max(
        Math.abs(nearest.east - visitor.longitude),
        Math.abs(nearest.west - visitor.longitude),
      );
      return boxAround(
        visitor,
        Math.max(START_SPAN_DEGREES, latReach * 2 * NEAREST_HOME_MARGIN),
        Math.max(START_SPAN_DEGREES, lonReach * 2 * NEAREST_HOME_MARGIN),
      );
    }
  }
  return null;
};

/**
 * Where the map opens on a plain market view: around the visitor, from
 * Cloudflare's location. A market page opens there only when the visitor is
 * inside its market; the plain homes page (`anywhere`) always does. Returns
 * 'pending' while it is decided, then the area, or null for the market.
 */
export const useStartArea = ({
  enabled,
  anywhere,
  marketBounds,
  marketShape,
  filters,
}: {
  enabled: boolean;
  anywhere: boolean;
  marketBounds: MapEnvelope | null;
  marketShape: MarketShape;
  filters: MapFilters;
}): MapEnvelope | null | 'pending' => {
  const [result, setResult] = useState<MapEnvelope | null | undefined>(undefined);
  const startedRef = useRef(false);
  // The first values decide where the map opens; later ones do not move it.
  const inputsRef = useRef({ anywhere, marketBounds, marketShape, filters });
  useEffect(() => {
    if (result !== undefined) return undefined;
    if (!enabled) {
      // A page that leaves its plain market view first opens where it asks.
      if (startedRef.current) setResult(null);
      return undefined;
    }
    startedRef.current = true;
    let active = true;
    const controller = new AbortController();
    const budget = setTimeout(() => controller.abort(), START_AREA_BUDGET_MS);
    const decide = async () => {
      const inputs = inputsRef.current;
      const visitor = await fetchVisitorLocation(controller.signal);
      if (!visitor) return null;
      if (
        !inputs.anywhere &&
        !(inputs.marketBounds && isInsideMarket(visitor, inputs.marketBounds, inputs.marketShape))
      ) {
        return null;
      }
      return findStartArea(visitor, inputs.filters, controller.signal);
    };
    void decide()
      .catch((error: unknown) => {
        // A stopped search is expected; any other failure opens the market quietly.
        if (!controller.signal.aborted) {
          reportMapError('marketplace-map-start-area', error);
        }
        return null;
      })
      .then((area) => {
        // A search the page stopped, as on unmount, decides nothing.
        if (active) setResult(controller.signal.aborted ? null : area);
      })
      .finally(() => clearTimeout(budget));
    return () => {
      active = false;
      clearTimeout(budget);
      controller.abort();
    };
  }, [enabled, result]);
  if (!enabled) return null;
  return result === undefined ? 'pending' : result;
};
